// components/admin/PaymentVerification/index.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatCurrency } from '../../../utils/format';
import { toast } from 'sonner';
import { Eye, Check, X, Download, Building2 } from 'lucide-react';
import { 
    fetchPendingPayments,
    verifyPayment,
    rejectPayment,
    selectPendingPayments,
    selectPaymentsLoading
} from '../../../store/slices/paymentSlice';
import { Button } from '../../../styles/components';
import LoadingScreen from '../../common/loading/LoadingScreen';
import {
    VerificationContainer,
    VerificationHeader,
    PaymentsList,
    PaymentCard,
    PaymentInfo,
    AmountInfo,
    ProofSection,
    VerificationActions,
    Modal,
    ModalContent,
    ImagePreview,
    RejectForm
} from './styles';

const PaymentVerification = () => {
    const dispatch = useDispatch();
    const payments = useSelector(selectPendingPayments);
    const loading = useSelector(selectPaymentsLoading);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        dispatch(fetchPendingPayments());
    }, [dispatch]);

    const handleVerify = async (payment) => {
        try {
            setProcessing(true);
            await dispatch(verifyPayment(payment.id)).unwrap();
            toast.success('Payment verified successfully', {
				style: { padding: "1rem" },
			});
        } catch (error) {
            toast.error(error.message || 'Failed to verify payment', {
				style: { padding: "1rem" },
			});
        } finally {
            setProcessing(false);
            setSelectedPayment(null);
        }
    };

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            toast.error('Please provide a reason for rejection', {
				style: { padding: "1rem" },
			});
            return;
        }

        try {
            setProcessing(true);
            await dispatch(rejectPayment({
                paymentId: selectedPayment.id,
                reason: rejectReason
            })).unwrap();
            toast.success('Payment rejected', {
				style: { padding: "1rem" },
			});
        } catch (error) {
            toast.error(error.message || 'Failed to reject payment', {
				style: { padding: "1rem" },
			});
        } finally {
            setProcessing(false);
            setShowRejectModal(false);
            setSelectedPayment(null);
            setRejectReason('');
        }
    };

    if (loading) {
        return <LoadingScreen/>;
    }

    return (
        <VerificationContainer>
            <VerificationHeader>
                <h2>Payment Verification</h2>
                <p>{payments.length} payments pending verification</p>
            </VerificationHeader>

            <PaymentsList>
                {payments.map(payment => (
                    <PaymentCard key={payment.id}>
                        <PaymentInfo>
                            <div className="user-info">
                                <h3>{payment.userData?.name}</h3>
                                <p>{payment.userData?.email}</p>
                            </div>
                            <div className="plan-info">
                                <span className="plan-name">{payment.plan.name} Plan</span>
                                <span className="date">
                                    Submitted: {new Date(payment.paymentDetails.submittedAt.toDate()).toLocaleDateString()}
                                </span>
                            </div>
                        </PaymentInfo>

                        <AmountInfo>
                            <div className="amount-details">
                                <div className="amount-row">
                                    <span>Base Amount:</span>
                                    <span>{formatCurrency(payment.basePrice)}</span>
                                </div>
                                {payment.discount > 0 && (
                                    <div className="amount-row discount">
                                        <span>Discount:</span>
                                        <span>-{formatCurrency(payment.basePrice - payment.finalPrice)}</span>
                                    </div>
                                )}
                                <div className="amount-row total">
                                    <span>Total Amount:</span>
                                    <span>{formatCurrency(payment.finalPrice)}</span>
                                </div>
                            </div>
                        </AmountInfo>

                        <ProofSection>
                            <Button 
                                variant="outlined"
                                onClick={() => setSelectedPayment(payment)}
                            >
                                <Eye size={18} />
                                View Proof
                            </Button>
                            <a 
                                href={payment.paymentDetails.proofUrl}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button variant="outlined">
                                    <Download size={18} />
                                    Download
                                </Button>
                            </a>
                        </ProofSection>

                        <VerificationActions>
                            <Button 
                                variant="outlined"
                                onClick={() => {
                                    setSelectedPayment(payment);
                                    setShowRejectModal(true);
                                }}
                                disabled={processing}
                            >
                                <X size={18} />
                                Reject
                            </Button>
                            <Button
                                onClick={() => handleVerify(payment)}
                                disabled={processing}
                            >
                                <Check size={18} />
                                Verify
                            </Button>
                        </VerificationActions>
                    </PaymentCard>
                ))}

                {payments.length === 0 && (
                    <div className="empty-state">
                        <p>No pending payments to verify</p>
                    </div>
                )}
            </PaymentsList>

            {/* Payment Proof Modal */}
            {selectedPayment && !showRejectModal && (
                <Modal onClick={() => setSelectedPayment(null)}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <h3>Payment Proof</h3>
                        {selectedPayment.paymentDetails.proofUrl.endsWith('.pdf') ? (
                            <iframe
                                src={selectedPayment.paymentDetails.proofUrl}
                                width="100%"
                                height="500px"
                                title="Payment Proof"
                            />
                        ) : (
                            <ImagePreview>
                                <img 
                                    src={selectedPayment.paymentDetails.proofUrl} 
                                    alt="Payment proof" 
                                />
                            </ImagePreview>
                        )}
                    </ModalContent>
                </Modal>
            )}

            {/* Reject Modal */}
            {showRejectModal && (
                <Modal onClick={() => setShowRejectModal(false)}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <h3>Reject Payment</h3>
                        <RejectForm>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Please provide a reason for rejection..."
                                rows={4}
                            />
                            <div className="actions">
                                <Button
                                    variant="outlined"
                                    onClick={() => setShowRejectModal(false)}
                                    disabled={processing}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleReject}
                                    disabled={!rejectReason.trim() || processing}
                                >
                                    {processing ? 'Processing...' : 'Confirm Rejection'}
                                </Button>
                            </div>
                        </RejectForm>
                    </ModalContent>
                </Modal>
            )}
        </VerificationContainer>
    );
};

export default PaymentVerification;